import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAdmin: false,
  loading: true,
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null }),
  signOut: async () => ({ error: null }),
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Pull users.is_admin so admin-only routes can gate render. Service-role
  // checks live on the API; this is purely UX (don't show admin UI to
  // non-admins). Failures here are non-fatal.
  //
  // Important: on transient failure (timeout / network blip) we preserve
  // the previous isAdmin value rather than resetting to false. Supabase
  // fires onAuthStateChange on every tab focus / token refresh, so a
  // momentary failure would otherwise flicker the admin nav off and on.
  // Only an explicit `is_admin = false` result or sign-out clears it.
  const lastFetchedUserRef = useRef<string | null>(null);
  const refreshAdmin = useCallback(async (u: User | null) => {
    if (!u) {
      lastFetchedUserRef.current = null;
      setIsAdmin(false);
      return;
    }
    // Skip redundant fetches if we already resolved this user successfully -
    // onAuthStateChange fires often and we don't need to re-query on every
    // tab focus.
    if (lastFetchedUserRef.current === u.id) return;

    console.log('[auth] refreshAdmin: fetching is_admin for', u.id);
    try {
      const result = await Promise.race([
        supabase.from('users').select('is_admin').eq('id', u.id).maybeSingle(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('is_admin fetch timed out after 3s')), 3000),
        ),
      ]);
      const { data, error } = result as { data: { is_admin: boolean } | null; error: any };
      if (error) {
        // Keep prior value on error - don't clobber a known-true admin flag.
        console.warn('[auth] is_admin select error (keeping prior value):', error.message);
        return;
      }
      console.log('[auth] refreshAdmin: done, is_admin =', !!data?.is_admin);
      lastFetchedUserRef.current = u.id;
      setIsAdmin(!!data?.is_admin);
    } catch (err: any) {
      // Timeout / network error - keep prior value.
      console.warn('[auth] refreshAdmin failed (keeping prior value):', err?.message);
    }
  }, []);

  useEffect(() => {
    // Belt-and-braces: even if Supabase's getSession() or the admin
    // select hangs indefinitely (seen in the wild on cold serverless
    // edges / flaky CDNs), force loading=false after 5s so route guards
    // fall through to /signin instead of spinning forever.
    const hardTimeout = setTimeout(() => {
      setLoading((current) => {
        if (current) console.warn('Auth init timed out after 5s - unblocking UI');
        return false;
      });
    }, 5000);

    // Always clear `loading` in a finally so a failed session/admin fetch
    // (network blip, RLS denial) can never leave the whole app behind a
    // blank screen.
    (async () => {
      try {
        console.log('[auth] init: calling getSession');
        const { data: { session } } = await supabase.auth.getSession();
        console.log('[auth] init: getSession returned, session?', !!session);
        const u = session?.user ?? null;
        setUser(u);
        await refreshAdmin(u);
      } catch (err) {
        console.error('Auth init failed', err);
      } finally {
        clearTimeout(hardTimeout);
        setLoading(false);
      }
    })();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      try {
        const u = session?.user ?? null;
        setUser(u);
        await refreshAdmin(u);
      } catch (err) {
        console.error('Auth state change failed', err);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      clearTimeout(hardTimeout);
      subscription.unsubscribe();
    };
  }, [refreshAdmin]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  }, []);

  const value = useMemo(() => ({
    user,
    isAdmin,
    loading,
    signIn,
    signUp,
    signOut,
  }), [user, isAdmin, loading, signIn, signUp, signOut]);

  // Render children unconditionally - individual guards (ProtectedRoute,
  // AdminRoute) gate their own subtrees on `loading`. Gating the whole
  // provider meant a slow/hung session call showed a blank page instead
  // of the Suspense spinner or any public route.
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
